/*
Usage:
<div class="grid grid-cols-3">
    <div
        v-for="(item, index) in items"
        :key="index"
        tabindex="0"
        @keydown.up="onArrow('up', $event)"
        @keydown.right="onArrow('right', $event)"
        @keydown.down="onArrow('down', $event)"
        @keydown.left="onArrow('left', $event)"
    >
        {{ item.name }} <a :href="item.link">{{ item.linkText }}</a>
    </div>
</div>

<script setup lang="ts">
import { onArrowNavigation } from '@/utils/helper/grid-arrow-navigation'

const items = ref([
    {
        name: 'Item 1',
        link: 'link1.com',
        linkText: 'Go to link 1'
    },
    {
        name: 'Item 2',
        link: 'link1.com',
        linkText: 'Go to link 1'
    },
    {
        name: 'Item 3',
        link: 'link1.com',
        linkText: 'Go to link 1'
    },
    {
        name: 'Item 4',
        link: 'link1.com',
        linkText: 'Go to link 1'
    }
])
</script>
*/
export function onArrowNavigation (direction: 'up' | 'right' | 'down' | 'left', event: Event) {
    event.preventDefault()

    const gridEl = (event.target as HTMLElement)?.closest('.grid') as HTMLElement
    if (!gridEl) return

    const children = Array.from(gridEl.children)
    const index = children.findIndex(child => child.contains(event.target as Node))
    if (index === -1) return

    // Calculate the grid dimensions
    const style = getComputedStyle(gridEl)
    const colCount = style.gridTemplateColumns.split(' ').length
    const rowCount = style.gridTemplateRows.split(' ').length

    // Calculate the offset (if any) and the current position
    const offset = Number(getComputedStyle(children[0]!).gridColumnStart) - 1 || 0
    const position = index + offset

    // Calculate the current row and column
    const currentRow = Math.floor(position / colCount)
    const currentCol = position % colCount

    let targetRow = currentRow
    let targetCol = currentCol

    // Determine the target position based on the direction
    switch (direction) {
    case 'up':
        targetRow = Math.max(0, currentRow - 1)
        break
    case 'right':
        // If at the end of a row and not on the last row, wrap to the next row
        if (currentCol === colCount - 1 && currentRow < rowCount - 1) {
            targetCol = 0
            targetRow++
        } else {
            targetCol = Math.min(colCount - 1, currentCol + 1)
        }
        break
    case 'down':
        targetRow = Math.min(rowCount - 1, currentRow + 1)
        break
    case 'left':
        // If at the start of a row and not on the first row, wrap to the previous row
        if (currentCol === 0 && currentRow > 0) {
            targetCol = colCount - 1
            targetRow--
        } else {
            targetCol = Math.max(0, currentCol - 1)
        }
        break
    }

    // Calculate the index of the target element and focus it
    const targetIndex = targetRow * colCount + targetCol - offset
    const target = children[targetIndex] as HTMLElement
    target?.focus()
}
